import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const fieldChipsKey = new PluginKey<DecorationSet>('emailTemplateFieldChips');
// Same as fieldReferencePattern in pkg/service/api/emailtemplate; the test checks.
// oxlint-disable-next-line eslint/no-useless-escape
export const fieldReferencePattern = /\[\[([^\[\]\r\n]+)\]\]/gu;

// No side padding, so fields written together touch the way their values do when sent.
// dark-mode-exempt: chips sit on the editor's white paper in both themes.
export const fieldChipClassName = 'rounded bg-sky-500/15 py-0.5 font-medium text-sky-700';

// Go's unicode.IsSpace, which the server splits labels on. JS's \s differs: it takes U+FEFF and
// leaves out U+0085.
const labelWhitespacePattern =
  /[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/u;

/** Lowercase with single spaces, the way the server compares field labels. */
export const normalizeFieldLabel = (label: string): string =>
  label.toLowerCase().split(labelWhitespacePattern).filter(Boolean).join(' ');

type FieldReferenceMatch = RegExpExecArray & { index: number };

/** Each `[[label]]` in the text. A blank `[[ ]]` has no label, so it isn't a field. */
const fieldReferencesIn = (text: string): FieldReferenceMatch[] =>
  [...text.matchAll(fieldReferencePattern)].filter(
    (match): match is FieldReferenceMatch =>
      match.index !== undefined && normalizeFieldLabel(match[1]) !== ''
  );

export type TextPart = { text: string; isFieldReference: boolean };

/** The text split around each `[[label]]`, in order, with no empty parts. */
export const splitAtFieldReferences = (text: string): TextPart[] => {
  const parts: TextPart[] = [];
  let textStart = 0;
  for (const match of fieldReferencesIn(text)) {
    parts.push(
      { text: text.slice(textStart, match.index), isFieldReference: false },
      { text: match[0], isFieldReference: true }
    );
    textStart = match.index + match[0].length;
  }
  parts.push({ text: text.slice(textStart), isFieldReference: false });
  return parts.filter((part) => part.text !== '');
};

/**
 * A reference split by markup stays plain text: joining text across nodes would chip
 * unrelated inline elements.
 */
const buildFieldChips = (doc: ProseMirrorNode): DecorationSet => {
  const chips: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;

    for (const match of fieldReferencesIn(node.text)) {
      const from = pos + match.index;
      chips.push(
        Decoration.inline(from, from + match[0].length, {
          class: fieldChipClassName,
          'data-email-template-field': 'true'
        })
      );
    }
  });
  return DecorationSet.create(doc, chips);
};

/**
 * Shows each `[[label]]` as a chip. The chip is a view decoration, so the saved HTML keeps the
 * plain `[[label]]` text the server reads.
 */
export const EmailTemplateFieldChips = Extension.create({
  name: 'emailTemplateFieldChips',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: fieldChipsKey,
        state: {
          init: (_, state) => buildFieldChips(state.doc),
          apply: (tr, chips) => (tr.docChanged ? buildFieldChips(tr.doc) : chips)
        },
        props: {
          decorations(state) {
            return fieldChipsKey.getState(state);
          }
        }
      })
    ];
  }
});
